import React, { useEffect, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CloseIcon from "@mui/icons-material/Close";

interface KnowledgeGraphProps {
  structuredData: {
    finding_description: string;
    primary_diagnosis: string;
    differential_diagnoses: string[];
    finding_location: string;
    characteristics: {
      color: string;
      size: string;
      shape: string;
      texture: string;
    };
    risk_factors: string[];
    recommendations: string[];
    malignancy_risk: string;
  };
}

interface GraphNode {
  id: string;
  name: string;
  val: number;
  color: string;
  type: string;
}

interface GraphLink {
  source: string | { id: string };
  target: string | { id: string };
  label?: string;
  color?: string;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

export default function KnowledgeGraph({ structuredData }: KnowledgeGraphProps) {
  const theme = useTheme();
  const fgRef = useRef<any>();
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [hoverNode, setHoverNode] = useState<any>(null);
  const [engineStopped, setEngineStopped] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  useEffect(() => {
    if (!structuredData) return;

    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];

    // Color palette based on theme
    const colors = {
      primary: theme.palette.primary.main,
      diagnosis: theme.palette.error.main,
      differential: theme.palette.warning.main,
      location: theme.palette.info.main,
      characteristics: theme.palette.secondary.main,
      risk: theme.palette.error.light,
      recommendation: theme.palette.success.main,
      malignancy: 
        structuredData.malignancy_risk === "malignant" ? theme.palette.error.dark :
        structuredData.malignancy_risk === "premalignant" ? theme.palette.warning.main :
        structuredData.malignancy_risk === "benign" ? theme.palette.success.main :
        theme.palette.grey[500],
    };

    // Central node - Primary Diagnosis
    nodes.push({
      id: "primary",
      name: structuredData.primary_diagnosis,
      val: 30,
      color: colors.diagnosis,
      type: "Primary Diagnosis",
    });

    // Malignancy Risk node
    nodes.push({
      id: "malignancy",
      name: `Risk: ${structuredData.malignancy_risk.toUpperCase()}`,
      val: 20,
      color: colors.malignancy,
      type: "Malignancy Risk",
    });
    links.push({
      source: "primary",
      target: "malignancy",
      label: "has risk",
      color: alpha(colors.malignancy, 0.4),
    });

    // Location node
    if (structuredData.finding_location) {
      nodes.push({
        id: "location",
        name: structuredData.finding_location,
        val: 15,
        color: colors.location,
        type: "Location",
      });
      links.push({
        source: "primary",
        target: "location",
        label: "located at",
        color: alpha(colors.location, 0.4),
      });
    }

    // Differential diagnoses
    structuredData.differential_diagnoses?.forEach((diff, idx) => {
      const diffId = `diff_${idx}`;
      nodes.push({
        id: diffId,
        name: diff,
        val: 12,
        color: colors.differential,
        type: "Differential",
      });
      links.push({
        source: "primary",
        target: diffId,
        label: "differential",
        color: alpha(colors.differential, 0.3),
      });
    });

    // Characteristics cluster
    const charKeys = Object.keys(structuredData.characteristics) as Array<
      keyof typeof structuredData.characteristics
    >;
    charKeys.forEach((key) => {
      const value = structuredData.characteristics[key];
      if (value && value !== "unknown") {
        const charId = `char_${key}`;
        nodes.push({
          id: charId,
          name: `${key}: ${value}`,
          val: 10,
          color: colors.characteristics,
          type: "Characteristic",
        });
        links.push({
          source: "primary",
          target: charId,
          label: "has",
          color: alpha(colors.characteristics, 0.3),
        });
      }
    });

    // Risk factors
    structuredData.risk_factors?.forEach((risk, idx) => {
      const riskId = `risk_${idx}`;
      nodes.push({
        id: riskId,
        name: risk,
        val: 10,
        color: colors.risk,
        type: "Risk Factor",
      });
      links.push({
        source: "primary",
        target: riskId,
        label: "risk",
        color: alpha(colors.risk, 0.3),
      });
    });

    // Recommendations
    structuredData.recommendations?.forEach((rec, idx) => {
      const recId = `rec_${idx}`;
      nodes.push({
        id: recId,
        name: rec.length > 40 ? rec.substring(0, 40) + "..." : rec,
        val: 10,
        color: colors.recommendation,
        type: "Recommendation",
      });
      links.push({
        source: "primary",
        target: recId,
        label: "recommends",
        color: alpha(colors.recommendation, 0.3),
      });
    });

    setGraphData({ nodes, links });
    
    // Reset engine state when data changes
    setEngineStopped(false);
    setSelectedNode(null);
  }, [structuredData, theme]);

  const handleNodeClick = (node: any) => {
    if (!node || !engineStopped) return;
    
    // Only set as selected node - don't move the camera
    setSelectedNode(node);
  };

  const handleNodeHover = (node: any) => {
    // Only allow hover when engine has stopped to prevent erratic behavior
    if (!engineStopped) return;
    
    setHoverNode(node || null);
    
    if (node) {
      const neighbors = new Set();
      const neighborLinks = new Set();
      
      graphData.links.forEach(link => {
        if (link.source === node.id || link.target === node.id) {
          neighborLinks.add(link);
          neighbors.add(typeof link.source === 'string' ? link.source : (link.source as { id: string }).id);
          neighbors.add(typeof link.target === 'string' ? link.target : (link.target as { id: string }).id);
        }
      });
      
      setHighlightNodes(neighbors);
      setHighlightLinks(neighborLinks);
    } else {
      setHighlightNodes(new Set());
      setHighlightLinks(new Set());
    }
  };

  const handleZoomIn = () => {
    if (fgRef.current && engineStopped) {
      const currentZoom = fgRef.current.zoom();
      const newZoom = Math.min(currentZoom * 1.5, 5); // Max zoom 5x, more aggressive multiplier
      fgRef.current.zoom(newZoom, 500);
    }
  };

  const handleZoomOut = () => {
    if (fgRef.current && engineStopped) {
      const currentZoom = fgRef.current.zoom();
      const newZoom = Math.max(currentZoom / 1.5, 0.3); // Min zoom 0.3x, more aggressive divisor
      fgRef.current.zoom(newZoom, 500);
    }
  };

  const handleCenter = () => {
    if (fgRef.current && engineStopped) {
      setSelectedNode(null);
      fgRef.current.zoomToFit(600, 50);
    }
  };

  return (

    <Box>
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          height: { xs: 400, sm: 500, md: 600 },
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.background.paper, 0.95),
        }}
      >
        {/* Controls */}
        <Stack
          spacing={1}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <Tooltip title="Zoom In" placement="left">
            <IconButton
              size="small"
              onClick={handleZoomIn}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                "&:hover": { bgcolor: theme.palette.background.paper },
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out" placement="left">
            <IconButton
              size="small"
              onClick={handleZoomOut}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                "&:hover": { bgcolor: theme.palette.background.paper },
              }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Center View" placement="left">
            <IconButton
              size="small"
              onClick={handleCenter}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                "&:hover": { bgcolor: theme.palette.background.paper },
              }}
            >
              <CenterFocusStrongIcon />
            </IconButton>
          </Tooltip>
        </Stack>

      {/* Hover tooltip */}
      {hoverNode && engineStopped && !selectedNode && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            p: 2,
            borderRadius: 1,
            maxWidth: 250,
            boxShadow: 3,
            border: `2px solid ${hoverNode.color}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: hoverNode.color }}>
            {hoverNode.type}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {hoverNode.name}
          </Typography>
        </Box>
      )}

      {/* Selected Node Details Dialog - Overlay */}
      {selectedNode && engineStopped && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 15,
            bgcolor: alpha(theme.palette.background.paper, 0.98),
            p: 3,
            borderRadius: 2,
            maxWidth: 400,
            minWidth: 300,
            boxShadow: `0 12px 48px ${alpha(theme.palette.common.black, 0.3)}`,
            border: `3px solid ${selectedNode.color}`,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Chip
                label={selectedNode.type}
                size="small"
                sx={{
                  bgcolor: selectedNode.color,
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setSelectedNode(null)}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 600, color: selectedNode.color }}>
              {selectedNode.name}
            </Typography>
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Node Details
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Category:</strong> {selectedNode.type}
                </Typography>
                <Typography variant="body2">
                  <strong>Connections:</strong> {
                    graphData.links.filter(link => 
                      link.source === selectedNode.id || link.target === selectedNode.id
                    ).length
                  } related nodes
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Loading indicator */}
      {!engineStopped && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            p: 2,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Generating graph layout...
          </Typography>
        </Box>
      )}

      {/* Force Graph */}
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        nodeLabel="name"
        nodeAutoColorBy="type"
        nodeCanvasObject={(node: any, ctx, globalScale) => {
          const label = node.name;
          const fontSize = 12 / globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          
          // Highlight effect
          const isHighlight = highlightNodes.has(node.id) || hoverNode?.id === node.id;
          const isSelected = selectedNode?.id === node.id;
          const nodeSize = node.val || 10;
          const radius = Math.sqrt(nodeSize) * (isHighlight || isSelected ? 1.5 : 1);

          // Draw node circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          ctx.fillStyle = node.color;
          ctx.fill();
          
          // Add border for highlighted or selected nodes
          if (isHighlight || isSelected) {
            ctx.strokeStyle = isSelected ? theme.palette.success.main : theme.palette.primary.main;
            ctx.lineWidth = (isSelected ? 3 : 2) / globalScale;
            ctx.stroke();
          }

          // Draw label
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = theme.palette.text.primary;
          const maxWidth = 100;
          const words = label.split(" ");
          let line = "";
          let y = node.y + radius + 10 / globalScale;

          for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + " ";
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth / globalScale && n > 0) {
              ctx.fillText(line, node.x, y);
              line = words[n] + " ";
              y += fontSize * 1.2;
            } else {
              line = testLine;
            }
          }
          ctx.fillText(line, node.x, y);
        }}
        linkLabel="label"
        linkWidth={(link: any) => (highlightLinks.has(link) ? 3 : 1)}
        linkDirectionalParticles={(link: any) => (highlightLinks.has(link) ? 4 : 0)}
        linkDirectionalParticleWidth={(link: any) => (highlightLinks.has(link) ? 4 : 2)}
        linkColor={(link: any) => link.color || alpha(theme.palette.text.secondary, 0.3)}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        enableNodeDrag={engineStopped}
        enableZoomInteraction={engineStopped}
        enablePanInteraction={engineStopped}
        cooldownTicks={150}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        onEngineStop={() => {
          setEngineStopped(true);
          if (fgRef.current) {
            // Initial fit with padding, only once
            setTimeout(() => {
              fgRef.current.zoomToFit(600, 60);
            }, 100);
          }
        }}
      />
    </Paper>

      {/* Legend - Below the KG box */}
      <Paper
        elevation={6}
        sx={{
          mt: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.98),
          p: 2,
          borderRadius: 2,
          maxWidth: 220,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: "block", color: theme.palette.primary.main }}>
          Legend
        </Typography>
        <Stack spacing={0.75}>
          <Chip label="Primary Diagnosis" size="small" sx={{ bgcolor: theme.palette.error.main, color: "#fff", height: 22, fontWeight: 600 }} />
          <Chip label="Differential" size="small" sx={{ bgcolor: theme.palette.warning.main, color: "#fff", height: 22, fontWeight: 600 }} />
          <Chip label="Characteristic" size="small" sx={{ bgcolor: theme.palette.secondary.main, color: "#fff", height: 22, fontWeight: 600 }} />
          <Chip label="Recommendation" size="small" sx={{ bgcolor: theme.palette.success.main, color: "#fff", height: 22, fontWeight: 600 }} />
        </Stack>
      </Paper>
    </Box>
  );
}